import { createApiClient } from '@aspid/api-client';

import { queries } from '@api';
import { queryClient } from '@api/api.queryClient.ts';
import { ApiUrls } from '@api/api.urls';

import { HttpClient } from './HttpClient';

export { UNKNOWN_SERVER_ERROR_MESSAGE } from '@aspid/api-client';

/**
 * Обвязка приложения поверх общего клиента.
 *
 * Транспорт, разбор ошибок и защита от рекурсии на 401 живут в
 * `@aspid/api-client`; здесь остаётся только то, что знает приложение: свой
 * `HttpClient`, свои ключи запросов и свой URL разлогинивания.
 *
 * Что сохранено при переезде (это приложение — источник обеих вещей, AF-13):
 * - сырое тело ответа в `ApiResponseError.data`, из которого `isRateLimit429`
 *   читает реквизиты плана при 429 (BACK-732);
 * - разбор формата `{ error, message }` — готовый текст приходит в `message`,
 *   а не в `detail`. Раньше это делал локальный `data.detail ?? data.message`.
 *
 * Что приложение приобретает: разбор field-level ошибок DRF и pydantic-формата
 * с `fields` для подсветки полей формы, сериализацию массивов в query
 * повторяющимися ключами и защиту от рекурсии, когда сам `/auth/force-logout`
 * отвечает 401.
 *
 * Изменение: на нераспознанном теле в `message` теперь приходит
 * «Неизвестная ошибка сервера», а не пустая строка — пустую строку нечего
 * показать пользователю.
 *
 * Путь модуля не менялся намеренно — импорты в остальном приложении
 * (`@api/apiUtils/makeAxiosRequest`) продолжают работать.
 */
const apiClient = createApiClient({
  httpClient: HttpClient,
  onUnauthorized: () => handleUnauthorizedError(),
});

export const { makeAxiosRequest } = apiClient;

export const forceLogOutUser = () =>
  makeAxiosRequest<undefined>({
    url: ApiUrls.auth.forceLogout,
    method: 'post',
  });

export const handleUnauthorizedError = async () => {
  await forceLogOutUser();
  await queryClient.invalidateQueries({
    queryKey: queries.users.activeUser.queryKey,
    refetchType: 'all',
  });
  // Обновление страницы
  globalThis.location.reload();
};
